import { Menu, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { HostTourGuideButton, TOUR, tourAttr } from '../../features/hostTour';
import { LanguageSwitcher } from '../LanguageSwitcher';

export interface NavItem {
  to: string;
  label: string;
  exact?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const adminNavGroups: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/admin/dashboard', label: 'Dashboard' },
      { to: '/admin/bookings', label: 'Bookings' },
      { to: '/admin/analytics', label: 'Analytics' },
    ],
  },
  {
    label: 'Activities',
    items: [
      { to: '/admin/activities', label: 'Activities' },
      { to: '/admin/passes', label: 'Passes' },
      { to: '/admin/picks', label: 'YOYO Picks' },
    ],
  },
  {
    label: 'Online Course',
    items: [
      { to: '/host/courses', label: 'Courses' },
      { to: '/admin/course-review', label: 'Course Review' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/admin/settlements', label: 'Settlements' },
      { to: '/admin/gift-cards', label: 'Gift Cards' },
      { to: '/admin/promotion-codes', label: 'Promos' },
      { to: '/admin/member-rewards', label: 'Member Rewards' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/admin/email', label: 'Email' },
      { to: '/admin/newsletter', label: 'Newsletter' },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/admin/users', label: 'Users' },
      { to: '/admin/hosts', label: 'Hosts' },
      { to: '/admin/reviews', label: 'Reviews' },
      { to: '/admin/investor-inquiries', label: 'Investor inquiries' },
    ],
  },
];

interface AdminLayoutProps {
  title: ReactNode;
  headerRight?: ReactNode;
  /** Flat nav for host-context pages; when omitted the grouped admin nav is used. */
  navItems?: NavItem[];
  children: ReactNode;
}

function useIsActive() {
  const { pathname } = useLocation();
  return (item: NavItem) => {
    if (item.exact) return pathname === item.to;
    return pathname.startsWith(item.to);
  };
}

function GroupedNav({ onNavigate }: { onNavigate?: () => void }) {
  const isActive = useIsActive();
  return (
    <nav className="flex-1 space-y-5 px-3 pb-4">
      {adminNavGroups.map((group) => (
        <div key={group.label}>
          <div className="px-2 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
            {group.label}
          </div>
          {group.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              aria-current={isActive(item) ? 'page' : undefined}
              className={
                isActive(item)
                  ? 'block rounded-md bg-primary-pale px-2 py-1.5 text-sm font-medium text-primary'
                  : 'block rounded-md px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }
            >
              {item.label}
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );
}

/** Back to the family site, styled like the header's language button. */
export function ViewSiteButton({ className = '' }: { className?: string }) {
  return (
    <Link
      to="/"
      className={`inline-flex h-10 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 ${className}`}
    >
      View Site
    </Link>
  );
}

function ViewSiteLink() {
  return (
    <div className="px-5 py-4">
      <ViewSiteButton className="w-full" />
    </div>
  );
}

function Header({
  title,
  headerRight,
  onOpenMenu,
  menuButtonRef,
}: Pick<AdminLayoutProps, 'title' | 'headerRight'> & {
  onOpenMenu?: () => void;
  menuButtonRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <header className="bg-white shadow-sm">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex min-h-10 flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="flex min-w-0 items-center gap-3">
            {onOpenMenu && (
              <button
                ref={menuButtonRef}
                type="button"
                onClick={onOpenMenu}
                className="-ml-1 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 lg:hidden"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}
            {typeof title === 'string' ? (
              <h1 className="text-2xl leading-8 font-bold text-gray-900">{title}</h1>
            ) : (
              title
            )}
          </div>
          <div className="ml-auto flex min-h-10 items-center gap-4">
            {headerRight}
            {/* The switcher is also the backend language preference (PRD §5.6). */}
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </header>
  );
}

export default function AdminLayout({ title, headerRight, navItems, children }: AdminLayoutProps) {
  const isActive = useIsActive();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  // Keyboard support for the mobile drawer: Escape closes, focus moves in on
  // open and back to the menu button on close, Tab is kept inside the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const drawer = drawerRef.current;
    const menuButton = menuButtonRef.current;
    const sidebar = sidebarRef.current;
    // Lock background scroll while the drawer is modal.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // The drawer only exists below lg; close it if the viewport crosses over.
    const desktop = window.matchMedia('(min-width: 1024px)');
    const onBreakpointChange = () => {
      if (desktop.matches) setDrawerOpen(false);
    };
    desktop.addEventListener('change', onBreakpointChange);
    drawer?.querySelector<HTMLElement>('a, button')?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        return;
      }
      if (e.key !== 'Tab' || !drawer) return;
      const focusables = drawer.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      desktop.removeEventListener('change', onBreakpointChange);
      document.body.style.overflow = prevOverflow;
      // After crossing to lg the menu button is display:none (offsetParent
      // null); send focus to the desktop sidebar's current page link instead.
      if (menuButton?.offsetParent) {
        menuButton.focus();
      } else {
        (
          sidebar?.querySelector<HTMLElement>('a[aria-current="page"]') ||
          sidebar?.querySelector<HTMLElement>('a')
        )?.focus();
      }
    };
  }, [drawerOpen]);

  // Host-context pages pass a short flat nav; keep the original tab-bar layout for them.
  if (navItems) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header title={title} headerRight={headerRight} />
        <nav className="bg-white" {...tourAttr(TOUR.navTabs)}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex space-x-4 overflow-x-auto sm:space-x-8">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={
                      isActive(item)
                        ? 'border-b-2 border-primary text-primary px-1 py-4 text-sm leading-5 font-medium whitespace-nowrap flex-shrink-0'
                        : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 px-1 py-4 text-sm leading-5 font-medium whitespace-nowrap flex-shrink-0'
                    }
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
              <div className="ml-4 flex flex-shrink-0 items-center gap-4">
                <HostTourGuideButton />
                <ViewSiteButton />
              </div>
            </div>
          </div>
        </nav>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 lg:flex">
      {/* Desktop sidebar */}
      <aside
        ref={sidebarRef}
        className="hidden w-56 shrink-0 border-r border-gray-200 bg-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:overflow-y-auto"
      >
        <div className="px-5 py-5 text-lg font-bold text-primary">YOYO Admin</div>
        <GroupedNav />
        <ViewSiteLink />
      </aside>

      <div className="min-w-0 flex-1">
        <Header
          title={title}
          headerRight={headerRight}
          onOpenMenu={() => setDrawerOpen(true)}
          menuButtonRef={menuButtonRef}
        />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className="absolute inset-0 bg-gray-900/50"
            onClick={() => setDrawerOpen(false)}
          />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Admin navigation"
            // dvh, and room below the last item, so Safari's bottom toolbar never hides it.
            className="absolute left-0 top-0 flex h-[100dvh] w-64 flex-col overflow-y-auto overscroll-contain bg-white pb-[calc(env(safe-area-inset-bottom)+6rem)] shadow-xl"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-lg font-bold text-primary">YOYO Admin</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close navigation"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* At the top on phones: always in view, never under the browser bar. */}
            <div className="px-5 pb-2">
              <ViewSiteButton className="w-full" />
            </div>
            <GroupedNav onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
